/* eslint-disable prettier/prettier */
/* eslint-disable no-sequences */
/* eslint-disable no-unused-expressions */
import { useContext, useEffect, useMemo, useState } from 'react';
import { ActionIcon, Alert, Box, Skeleton, Text, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconBulb, IconCalendarStats, IconCheck, IconRefresh } from '@tabler/icons';
import CalendarModal from '../Atoms/Calendar/CalendarModal';
import { WindowContext } from '../Service/Context/WindowContext';
import { getPrivateAndBusinessEvents } from '../Service/restapi/calendarAccountService';
import { EventContext } from '../Service/Context/EventContext';
import { ViolationContext } from '../Service/Context/ViolationContext';
import { useModalState } from './Dialogs';
import { LicenceS } from '../Service/LicenceS';
import { useParams } from 'react-router-dom';

const AvailabilityCheckV2 = () => {
  const [privateEvents, setPrivateEvents] = useState([]);
  const [businessEvents, setBusinessEvents] = useState([]);
  const { show, toggle } = useModalState(false);
  const [isLoading, setLoading] = useState(false);
  const [bookedForThisEvent, setBookedForThisEvent] = useState(undefined);
  const [doubleBooking, setDoubleBooking] = useState(undefined);
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const { xs } = useContext(WindowContext);

  const {  dates } = useContext(EventContext);
  const { id: eventId } = useParams();
  const regularDates = useMemo(() => dates.filter((ed) => ed.type === 'REGULAR_DATE'), [dates]);

  useEffect(() => {
    if (regularDates.length > 0) {
      fetchOtherEvents();
    }
  }, [regularDates]);

  function fetchOtherEvents() {
    setLoading(true);
    setDoubleBooking(undefined);
    setBookedForThisEvent(undefined);
    getPrivateAndBusinessEvents(regularDates, eventId)
      .then((resp) => {
        if (resp.ok) {
          resp.json().then((calendarEvents) => {
            const bookedEvents = Array.from(
              new Map(
                calendarEvents.businessEvents.filter((b) => b.bookingState === 'BOOKED').map((b) => [b.id, b]), // remove multiple, if they belong to the same event
              ).values(),
            );

            if (bookedEvents.length > 1) {
              setDoubleBooking(true);
            }
            const a = calendarEvents.businessEvents.filter((b) => b.id === eventId && b.bookingState === 'BOOKED');
            if (a.length > 0) {
              setBookedForThisEvent(true);
            }
            const bizEvents = calendarEvents.businessEvents.filter((e) => e.id !== eventId);
            setPrivateEvents(calendarEvents.privateEvents);
            setBusinessEvents(bizEvents);
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  if (regularDates.length === 0) {
    return null;
  }

  if (isLoading) {
    return <Skeleton width="100%" height={80} mb="sm" />;
  }
  const openBusinessEvents = businessEvents.filter((e) => e.bookingState === 'OPEN');
  const offeredBusinessEvents = businessEvents.filter((e) => e.bookingState === 'OFFERED');
  const bookedBusinessEvents = businessEvents.filter((e) => e.bookingState === 'BOOKED');

  const sx = {
    backgroundColor:
      bookedBusinessEvents.length > 0 || privateEvents.length > 0
        ? '#f073730f'
        : offeredBusinessEvents.length > 0
        ? 'rgba(232, 180, 112, 0.18)'
        : openBusinessEvents.length > 0
        ? 'rgba(114, 136, 154, 0.14)'
        : 'rgba(217, 241, 217, 0.18)',
    borderColor:
      bookedBusinessEvents.length > 0 || privateEvents.length > 0
        ? '#f5c6cb'
        : offeredBusinessEvents.length > 0
        ? '#fff4d7'
        : openBusinessEvents.length > 0
        ? '#dfe5ea'
        : 'rgb(229, 229, 229)',
    boxShadow: doubleBooking ? '0 0 0 0 rgba(0, 0, 0, 1)' : 'unset',
    transform: doubleBooking ? 'scale(1)' : 'unset',
    animation: doubleBooking ? 'pulse 2s infinite' : 'unset',
  };
  const sxDark = {
    backgroundColor:
      bookedBusinessEvents.length > 0 || privateEvents.length > 0
        ? '#80323233' // subtle dark red overlay
        : offeredBusinessEvents.length > 0
        ? 'rgba(232, 180, 112, 0.10)' // softened amber tone
        : openBusinessEvents.length > 0
        ? 'rgba(114, 136, 154, 0.10)' // cool neutral
        : 'rgba(90, 140, 90, 0.12)', // soft green tint

    borderColor:
      bookedBusinessEvents.length > 0 || privateEvents.length > 0
        ? '#cc7a7a' // muted red
        : offeredBusinessEvents.length > 0
        ? '#e8b470' // soft gold
        : openBusinessEvents.length > 0
        ? '#7a8a99' // gray-blue border
        : '#6a846a', // desaturated green

    boxShadow: doubleBooking ? '0 0 6px 2px rgba(255, 0, 0, 0.3)' : 'unset',
    transform: doubleBooking ? 'scale(1)' : 'unset',
    animation: doubleBooking ? 'pulse 2s infinite' : 'unset',
  };

  let color = 'green';
  let icon = <IconCheck />;
  let title = !xs
    ? bookedForThisEvent
      ? 'Gebucht für dieses Event'
      : 'Du bist verfügbar!'
    : bookedForThisEvent
    ? 'Gebucht!'
    : 'Verfügbar!';
  let subTitle = bookedForThisEvent
    ? 'Du bist für dieses Event gebucht.'
    : 'Du hast keine gebuchten Events oder privaten Kalender-Einträge.';

  if (openBusinessEvents.length > 0) {
    color = 'blue';
    (icon = <IconBulb />), (title = 'Offene Anfrage');
    subTitle = 'Du hast weitere offene Anfragen für diesen Tag.';
  }
  if (bookedForThisEvent && openBusinessEvents.length > 0) {
    color = 'blue';
    (icon = <IconBulb />), (title = 'Gebucht, aber andere offene Anfrage');
    subTitle = 'Du bist für dieses Event gebucht, aber Du hast offene Anfragen';
  }
  if (offeredBusinessEvents.length > 0) {
    color = 'orange';
    icon = <IconAlertCircle />;
    title = 'Anderem Kunden angeboten';
    subTitle = 'Du hast diesen Tag in einem anderen Event angeboten.';
  }
  if (bookedForThisEvent && offeredBusinessEvents.length > 0) {
    color = 'orange';
    icon = <IconAlertCircle />;
    title = 'Gebucht, aber anderem Kunden angeboten';
    subTitle = 'Du bist für dieses Event gebucht, hast Dich aber in einem anderen Event angeboten.';
  }
  if (bookedBusinessEvents.length > 0) {
    color = 'red';
    icon = <IconAlertCircle size={16} />;
    title = 'Bereits ausgebucht';
    subTitle = 'Du bist an diesem Tag bereits gebucht.';
  }
  if (privateEvents.length > 0) {
    color = 'red';
    icon = <IconAlertCircle color="red" size={16} />;
    (title = <Text color="red">Privater Termin</Text>), (subTitle = 'Du hast private Termine an diesem Tag.');
  }
  if (bookedBusinessEvents.length > 0 && privateEvents.length > 0) {
    color = 'red';
    icon = <IconAlertCircle color="red" size={16} />;
    (title = <Text color="red">Ausgebucht und privater Termin!</Text>),
      (subTitle = 'Du bist bereits gebucht und hast einen privaten Termin.');
  }
  if (doubleBooking) {
    color = 'red';
    icon = <IconAlertCircle color="red" size={16} />;
    (title = <Text color="red">Mehrere Buchungen!</Text>),
      (subTitle = 'Achtung, Du hast mehrere Buchungen für diesen Tag!');
  }

  return (
    <Alert
      p="xs"
      color={color}
      icon={icon}
      title={title}
      mb="sm"
      sx={(theme) => (theme.colorScheme === 'dark' ? sxDark : sx)}
      styles={{
        icon: {
          marginRight: 8,
        },
        wrapper: {
          width: '90%',
        },
      }}
    >
      {!xs && (
        <>
          <Text size="xs">{subTitle}</Text>
          <Box sx={{ position: 'absolute', right: '1%', top: '50%', transform: 'translate(-1%, -50%)' }}>
            <Tooltip label="Aktualisieren">
              <ActionIcon size="xs" onClick={() => fetchOtherEvents()}>
                <IconRefresh />
              </ActionIcon>
            </Tooltip>
            <ActionIcon
              size="xs"
              onClick={() => executeWithLicenceCheck(LicenceS.Restrictions.PRIVATE_CALENDAR, toggle)}
              mt="xs"
            >
              <IconCalendarStats />
            </ActionIcon>
          </Box>
          <CalendarModal dates={regularDates} eventId={eventId} show={show} toggle={toggle} />
        </>
      )}
    </Alert>
  );
};

export default AvailabilityCheckV2;
